import { Zap } from 'lucide-react';
import { LiveInsightCard } from './LiveInsightCard';
import { useLiveInsights } from '@/hooks/useLiveInsights';
import { useTeamSeasonMetrics } from '@/hooks/useTeamSeasonMetrics';
import { useGameStatistics } from '@/hooks/useGameStatistics';

interface LiveInsightsPanelProps {
  fixtureId: number;
  homeTeamId?: string;
  awayTeamId?: string;
  homeTeamName: string;
  awayTeamName: string;
  homeScore: number;
  awayScore: number;
  isLive: boolean;
}

export function LiveInsightsPanel({
  fixtureId,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  homeScore,
  awayScore,
  isLive,
}: LiveInsightsPanelProps) {
  const isZeroZero = isLive && homeScore === 0 && awayScore === 0;

  // Hooks always called (React rules) — but skip fetching when not needed
  const { homeMetrics, awayMetrics } = useTeamSeasonMetrics(
    isZeroZero ? homeTeamId : undefined,
    isZeroZero ? awayTeamId : undefined
  );

  const { data: statistics } = useGameStatistics(
    isZeroZero ? fixtureId : null,
    'live',
    isZeroZero
  );

  const stats = statistics ? {
    homeSot: statistics.home_shots_on_goal ?? 0,
    awaySot: statistics.away_shots_on_goal ?? 0,
    homeCorners: statistics.home_corners ?? 0,
    awayCorners: statistics.away_corners ?? 0,
    homeShots: statistics.home_total_shots ?? 0,
    awayShots: statistics.away_total_shots ?? 0,
  } : null;

  const insights = useLiveInsights({
    homeTeamName,
    awayTeamName,
    homeMetrics,
    awayMetrics,
    homeScore,
    awayScore,
    isLive,
    stats,
  });

  if (!isZeroZero || insights.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        <Zap className="h-3.5 w-3.5 text-[hsl(var(--sg7-gold))]" />
        <span>Live Contextual Insights</span>
      </div>
      <div className="grid gap-2">
        {insights.map((insight) => (
          <LiveInsightCard key={insight.id} insight={insight} />
        ))}
      </div>
    </div>
  );
}
